/**
 * Hook useLocalizeContent
 * Devuelve funciones para mostrar el contenido de `src/data/` en el idioma
 * activo (ver `i18n/localizeContent`). Al cambiar de idioma el componente se
 * vuelve a renderizar porque `useTranslation` está suscrito al cambio.
 *
 * Uso:
 *   const { lc, localize } = useLocalizeContent();
 *   lc(recipe.title)            // un campo
 *   localize(recipesData)       // un elemento o lista entera
 */

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { localizeContent, localizeDeep } from '../i18n/localizeContent';

/**
 * @returns {{ lc: (value: *) => *, localize: (value: *) => *, language: string }}
 *   `lc` resuelve un campo, `localize` un elemento o lista completa
 */
const useLocalizeContent = () => {
  const { i18n } = useTranslation();
  const language = i18n.language;

  const lc = useCallback((value) => localizeContent(value, language), [language]);
  const localize = useCallback((value) => localizeDeep(value, language), [language]);

  return useMemo(() => ({ lc, localize, language }), [lc, localize, language]);
};

export default useLocalizeContent;
